import { useTranslation } from '../i18n/I18nProvider'
import { Icon } from './icons/Icon'

/**
 * D267's fallback surface: a downloaded update that has not installed itself yet, said once on
 * Home.
 *
 * **It exists for the resident who never leaves the app.** The install happens on the next
 * background (`SeloGateStoreUpdatePlugin.handleOnPause`), so this is not the mechanism — it is the
 * one case the mechanism cannot reach, and it carries the same second consent the sidebar row
 * always did, on the screen the resident is already looking at rather than behind the hamburger.
 * **That is the whole of what D267 buys over D266**: the two taps became one plus a wait, and this
 * is what the second tap looks like when it is still needed.
 *
 * **It states that it will install by itself**, which is the sentence that makes the tap optional
 * rather than merely relocated. Without it the banner is a demand, and a demand is exactly what
 * D137 refuses to make.
 *
 * **Not dismissible, and the reason is that its life is already short.** A dismiss control would
 * need state that outlives a render, and the banner leaves on its own the moment the app is
 * backgrounded — which is the same gesture that installs the update. A resident who does not want
 * to act simply switches away, and the thing they were being asked for happens.
 *
 * **Muted residents never see it.** `useAppUpdate`'s `installReady` folds D266 §3.5's mute in,
 * because a banner is a louder report than the dot that setting already silences; the Update row
 * stays where it was, so nothing is taken away from them and their update still installs.
 */
export function UpdateReadyBanner({ onRestart }: { onRestart(): void }): JSX.Element {
  const { t } = useTranslation('drawer')
  return (
    /* `role="status"` rather than an alert: it is polite by design, announced after whatever the
       screen was already saying, and it interrupts nothing. */
    <div className="update-ready" role="status">
      <Icon name="refresh-cw" size={15} />
      <div className="update-ready-text">
        <b>{t('updateReady')}</b>
        <span>{t('updateReadyHint')}</span>
      </div>
      {/* **The same string the sidebar row wears** (`updateRestart`), deliberately shared rather
          than copied: the two surfaces name one tap, and a second key is how they come to describe
          it differently at the next retranslation. */}
      <button type="button" className="update-ready-action" onClick={onRestart}>
        {t('updateRestart')}
      </button>
    </div>
  )
}
